<script setup lang="ts">
import type { IAttemptResult } from './types'

const props = defineProps<{
  result: IAttemptResult
}>()

const emit = defineEmits<{
  retry: []
  done: []
}>()

const percentage = computed(() =>
  props.result.total_possible > 0
    ? Math.round((props.result.score / props.result.total_possible) * 100)
    : 0
)

const RADIUS = 42
const CIRCUMFERENCE = 2 * Math.PI * RADIUS
const strokeDashoffset = computed(() => CIRCUMFERENCE - (percentage.value / 100) * CIRCUMFERENCE)

const label = computed(() => {
  if (percentage.value < 40) return 'widget.quiz.results.label_poor'
  if (percentage.value < 70) return 'widget.quiz.results.label_good'
  return 'widget.quiz.results.label_great'
})

const wrongCount = computed(() => props.result.total_questions - props.result.correct_count)
</script>

<template>
  <div class="overflow-hidden rounded-lg bg-white p-6 text-center">
    <div class="mb-5">
      <p class="text-lg font-bold text-gray-900">{{ $t('widget.quiz.results.congrats') }}</p>
      <p class="text-xs font-semibold uppercase tracking-wide text-gray-500">{{ $t('widget.quiz.results.congrats_sub') }}</p>
    </div>

    <!-- SVG circle -->
    <div class="relative mx-auto mb-4 h-28 w-28">
      <svg viewBox="0 0 100 100" class="h-28 w-28 -rotate-90">
        <circle cx="50" cy="50" r="42" fill="none" stroke="#e2e8f0" stroke-width="8" />
        <circle
          cx="50"
          cy="50"
          r="42"
          fill="none"
          stroke="#2563eb"
          stroke-width="8"
          stroke-linecap="round"
          :stroke-dasharray="CIRCUMFERENCE"
          :stroke-dashoffset="strokeDashoffset"
          class="transition-all duration-700"
        />
      </svg>
      <div class="absolute inset-0 flex flex-col items-center justify-center">
        <span class="text-2xl font-extrabold text-gray-900">{{ percentage }}%</span>
      </div>
    </div>

    <p class="mb-1 text-lg font-bold text-gray-900">{{ $t(label) }}</p>
    <p class="mb-6 text-sm text-gray-500">
      {{ result.correct_count }} {{ $t('widget.quiz.results.correct_of') }}
      {{ result.total_questions }}
    </p>

    <!-- Stat tiles -->
    <div class="mb-6 grid grid-cols-3 gap-3">
      <div class="rounded-xl border border-green-100 bg-green-50 py-3">
        <p class="text-xl font-bold text-green-600">{{ result.correct_count }}</p>
        <p class="text-xs text-gray-500">{{ $t('widget.quiz.results.correct') }}</p>
      </div>
      <div class="rounded-xl border border-red-100 bg-red-50 py-3">
        <p class="text-xl font-bold text-red-500">
          {{ wrongCount }}
        </p>
        <p class="text-xs text-gray-500">{{ $t('widget.quiz.results.wrong') }}</p>
      </div>
      <div class="rounded-xl border border-blue-100 bg-blue-50 py-3">
        <p class="text-xl font-bold text-blue-600">{{ result.score }}</p>
        <p class="text-xs text-gray-500">{{ $t('widget.quiz.results.points') }}</p>
      </div>
    </div>

    <!-- Actions -->
    <div class="flex flex-col gap-2">
      <Button variant="blue" class="w-full" @click="emit('retry')">
        {{ $t('widget.quiz.results.retry') }}
      </Button>
    </div>
  </div>
</template>
